import React, { useEffect, useState } from 'react'
import { BulletSteps, EntityIndex, GlobalLoader, Modal, SelectInput } from '@reactiveonline/reactive_admin_shared-packages'
import Dropzone from 'react-dropzone'
import * as XLSX from 'xlsx'

const createOptions = [
  { key: 'importAccounts', icon: 'fa-upload', title: Partners.translations.tenant.import_accounts },
  { key: 'downloadTemplate', icon: 'fa-folder-arrow-down', title: Partners.translations.tenant.download_template }
]

export default function ImportAccountsModal({
  adminRole, closeModal, getOrganizationsPath, loading, registrationsPath, setLoading, importAccountsPath
}) {
  let steps = []

  if (adminRole === 'director_superadmin') {
    steps = [
      { key: 'choose_action', next: 'choose_organization' },
      { key: 'choose_organization', next: 'choose_file' },
      { key: 'choose_file', next: 'overview' },
      { key: 'overview', next: null }
    ]
  } else if (adminRole === 'organization_admin') {
    steps = [
      { key: 'choose_action', next: 'choose_file' },
      { key: 'choose_file', next: 'overview' },
      { key: 'overview', next: null }
    ]
  }

  const [currentStep, setCurrentStep] = useState(steps[0])
  const [selectedFile, setSelectedFile] = useState(null)
  const [availableOrganizations, setAvailableOrganizations] = useState([])
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [importedAccounts, setImportedAccounts] = useState([])
  const [tableHeadings, setTableHeadings] = useState([])
  const [errors, setErrors] = useState([])
  const [successMsg, setSuccessMsg] = useState(null)

  useEffect(() => {
    if (adminRole === 'director_superadmin') {
      setLoading(true)
  
      Rails.ajax({
        type: 'GET',
        url: getOrganizationsPath,
        dataType: 'json',
        success: res => {
          setAvailableOrganizations(res.items)
          setSelectedOrganization(res.items[0].value)
  
          setLoading(false)
        }
      })
    }
  }, [])

  function handleSelectedOption(optionSelected) {
    if (optionSelected !== 'downloadTemplate') {
      proceedToNextStep()
    } else {
      window.open('https://storage.googleapis.com/reactive_files_eu/bulk_import_template_partners.xlsx', '_blank')
    }
  }

  const proceedToNextStep = _e => {
    const nextStep = steps.find(step => step.key === currentStep.next)

    if (currentStep.key === 'overview') {
      if (!successMsg) {
        handleImport()
      } else {
        window.location.href = registrationsPath
      }

      return
    }

    setCurrentStep(nextStep)
  }

  const onDropFile = droppedFile => {
    const file = droppedFile[0]
    setSelectedFile(file)

    const reader = new FileReader() 
    const rABS = !!reader.readAsBinaryString

    reader.onload = (e) => {
      const bstr = e.target.result
      const workbook = XLSX.read(bstr, { type: rABS ? "binary" : "array" })
      const sheet_name_list = workbook.SheetNames[0]
      let jsonFromExcel = XLSX.utils.sheet_to_json(workbook.Sheets[sheet_name_list], {
          raw: false,
          dateNF: "MM-DD-YYYY",
          header:1,
          defval: ""
      })
      jsonFromExcel = jsonFromExcel.filter(el => !!el[0])
      const headers = jsonFromExcel[0]
      const items = jsonFromExcel.slice(1)

      setTableHeadings(headers)
      setImportedAccounts(items)

      proceedToNextStep()
    }

    if (rABS) {
      reader.readAsBinaryString(file)
    } else  {
      reader.readAsArrayBuffer(file)
    }
  }

  const handleBackClick = _e => {
    const previousStep = steps.find(step => step.next === currentStep.key)

    if (previousStep) {
      if (previousStep.key === 'choose_action') {
        setSelectedFile(null)
      }
      setCurrentStep(previousStep)
    }

    setErrors([])
    setSuccessMsg(null)
  }

  const renderSteps = () => {
    const currentStepIndex = steps.findIndex(step => step.key === currentStep.key)

    return (
      <BulletSteps
        stepsCount={ steps.length }
        currentStep={ currentStepIndex }
        setCurrentStep={ index => index < currentStepIndex ? setCurrentStep(steps[index]) : null }
      />
    )
  }

  const calculateRelevantActionText = () => {
    if (currentStep.key === 'overview') {
      if (successMsg) {
        return ReactiveCore.translations.close_action
      } else {
        return ReactiveCore.translations.confirm
      }
    } else {
      return ReactiveCore.translations.next
    }
  }

  const itemRenderer = item => (
    item.map((el, idx) => (
      <td key={ idx }>
        { el }
      </td>
    ))
  )
  

  const handleImport = () => {
    const fd = new FormData()

    fd.append('file', selectedFile)
    if (adminRole === 'director_superadmin') {
      fd.append('related_organization_id', selectedOrganization)
    }

    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: importAccountsPath,
      dataType: 'json',
      data: fd,
      success: _res => {
        setSuccessMsg(Partners.translations.tenant.import_started)
        setLoading(false)
      },
      error: res => {
        if (res.error) {
          setErrors([res.error])
        } else if (res.invalidRows.length > 1) {
          setErrors(res.invalidRows)
        }

        setLoading(false)
      }
    })
  }

  const renderOverview = () => {
    let jsx

    if (errors.length) {
      jsx = (
        <div 
          className='m-t-30 flex-box content-center'
          style={{ fontSize: '16px' }}
        >
          <div>
            <div 
              className='m-t-30 m-b-10 flex-box content-center items-center'
              style={{ fontSize: '16px', gap: '10px' }}
            >
              <i className='fa fa-circle-exclamation' />
              { Partners.translations.tenant.import_error }
            </div>

            <hr />

            { errors.map((item, index) => (
              <div key={ index }>
                { item }
              </div>
            ))}
          </div>
        </div>
      )
    } else if (successMsg) {
      jsx = (
        <div 
        className='m-t-30 m-b-10 flex-box content-center items-center'
        style={{ fontSize: '16px', gap: '10px' }}
      >
        <i className='fa fa-circle-exclamation' />
        { successMsg }
      </div>
      )
    } else {
      if (!importedAccounts.length) {
        jsx = (
          <div 
            className='m-t-30 flex-box content-center items-center'
            style={{ fontSize: '16px', gap: '10px' }}
          >
            <i className='fa fa-circle-exclamation' />
            { Partners.translations.tenant.excel_missing_data }
          </div>
        )
      } else {
        jsx = (
          <>
            <EntityIndex
              initialItems={ importedAccounts.length ? importedAccounts.slice(0, 5) : [[]] }
              initialTotalPages={ 1 }
              tableHeadings={ tableHeadings }
              itemRenderer={ itemRenderer }
              tabs={ [] }
              disableDelete
              disableSearch
              disableUrlUpdates
            />

            { importedAccounts.length > 5 &&
              <div             
                className='m-t-30 flex-box content-center items-center'
                style={{ fontSize: '16px', gap: '10px' }}
              >
                { `This file contains  ${ importedAccounts.length - 5 } more line${ importedAccounts.length > 6 ? 's' : '' }` }
              </div>
            }
          </>
        )
      }
    }

    return jsx
  }

  return (
    <>
      <Modal
        visible
        closeModal={ closeModal }
        mode='xlarge'
        cancelAction={ (currentStep.key !== 'choose_action' && !successMsg) ? handleBackClick : null }
        cancelActionText={ ReactiveCore.translations.back }
        cancelActionIcon='fa-thin fa-chevron-left'
        action={ (!['choose_action', 'choose_file'].includes(currentStep.key) && !errors.length) ? proceedToNextStep : null }
        actionText={ calculateRelevantActionText() }
        footerMiddleActions={ renderSteps }
        disableAction={ currentStep.key === 'overview' && !importedAccounts.length }
        showConfirmation={ currentStep.key === 'overview' && !!importedAccounts.length && !errors.length && !successMsg }
        confirmationText={ Partners.translations.tenant.bulk_import_confirmation }
      >
        <div className='flex-box flex-column content-center flex-1'>
          <div className='m-b-30'>
            <div className='flex-box items-center m-b-10 bold' style={{ textAlign: 'left', gap: 10 }}>
              <div>
                { Partners.translations.tenant.import_from_excel }
              </div>
            </div>
            <div style={{ height: 2, backgroundColor: '#DDD', width: 100, marginTop: 10 }}></div>
          </div>

          { currentStep.key === 'choose_action' &&
            <div
              className={ 'flex-box flex-column' }
              style={{ width: '100%', height: '100%', padding: '0 30px' }}
            >
              <div className='flex-box content-space-between m-t-30' style={{ width: '100%' }}>
                { createOptions.map( option => (
                  <div
                    key={ option.key }
                    className='flex-box flex-1 flex-column items-center content-space-between pointer'
                    style={{ minHeight: 300, padding: '50px 20px', lineHeight: '1.5em', border: '1px solid', borderRadius: 6, marginRight: '20px' }}
                    onClick={ () => handleSelectedOption(option.key) }
                  >
                    <i
                      className={ `fa-light ${ option.icon } color-scheme` }
                      style={{ fontSize: 45 }}
                    />
  
                    <div className='title bold m-t-30' style={{ textAlign: 'center' }}>
                      { option.title }
                    </div>
                  </div>
                ))}
              </div>
            </div>
          }

          { currentStep.key === 'choose_organization' &&
            <>
              <div className='m-b-30'>
                { Partners.translations.tenant.import_organization_help }
              </div>
              <div>
                <SelectInput
                  label={ Partners.translations.organization.model_name }
                  options={ availableOrganizations }
                  currentOption={ selectedOrganization }
                  setCurrentOption={ option => setSelectedOrganization(option.value) }
                />
              </div>
            </>
          }

          { currentStep.key === 'choose_file' && 
            <div className='flex-box tab-content'>
              <div className='gallery-content flex-box content-start flex-column'>
                <div className='flex-box items-center content-center'>
                  <Dropzone
                    onDrop={ onDropFile }
                    accept={ ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] }
                  >
                    { ({ getRootProps, getInputProps }) => (
                      <div
                        { ...getRootProps() }
                        className='dropzone-inner flex-box items-center content-center'
                      >
                        <input { ...getInputProps() } />
                        <i className='fa-light fa-rectangle-history-circle-plus' />
                      </div>
                    )}
                  </Dropzone>
                </div>
              </div>
            </div>
          }

          { currentStep.key === 'overview' && renderOverview() }
        </div>

        { loading && <GlobalLoader fullPage /> }
      </Modal>
    </>
  )
}